import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  PenTool, LogOut, Plus, Edit, Trash2, Eye, EyeOff, 
  FileText, Save, X, User, Newspaper, ArrowLeft,
  Bold, Italic, Link2, List, ListOrdered, Quote, ImageIcon
} from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Simple Rich Text Editor Component
const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border border-white/10">
      <div className="flex flex-wrap gap-1 p-2 bg-zinc-800 border-b border-white/10">
        <button type="button" onClick={() => execCommand("bold")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("italic")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { const url = prompt("Enter URL:"); if (url) execCommand("createLink", url); }} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Insert Link">
          <Link2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("insertUnorderedList")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("insertOrderedList")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("formatBlock", "blockquote")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Quote">
          <Quote className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { const url = prompt("Enter image URL:"); if (url) execCommand("insertImage", url); }} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Insert Image">
          <ImageIcon className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        className="min-h-[300px] p-4 bg-black text-white focus:outline-none prose prose-invert max-w-none"
        style={{ whiteSpace: "pre-wrap" }}
      />
    </div>
  );
};

export const WriterPortal = () => {
  const navigate = useNavigate();
  const [writer, setWriter] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  // Form state
  const [form, setForm] = useState({
    title: "",
    content: "",
    excerpt: "",
    featured_image: "",
    category: "General",
    tags: [],
    status: "draft"
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("writerToken");
    const writerInfo = localStorage.getItem("writerInfo");

    if (!token || !writerInfo) {
      navigate("/writer/login");
      return;
    }

    setWriter(JSON.parse(writerInfo));
    fetchArticles(token);
  };

  const fetchArticles = async (token) => {
    try {
      const res = await axios.get(`${API}/news/writer/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("writerToken");
    localStorage.removeItem("writerInfo");
    navigate("/writer/login");
  };

  const resetForm = () => {
    setForm({
      title: "",
      content: "",
      excerpt: "",
      featured_image: "",
      category: "General",
      tags: [],
      status: "draft"
    });
    setTagInput("");
    setEditingArticle(null);
  };

  const handleEdit = (article) => {
    setForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || "",
      featured_image: article.featured_image || "",
      category: article.category || "General",
      tags: article.tags || [],
      status: article.status
    });
    setEditingArticle(article);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.content) {
      alert("Title and content are required");
      return;
    }

    const token = localStorage.getItem("writerToken");
    setSaving(true);

    try {
      if (editingArticle) {
        await axios.put(
          `${API}/news/writer/articles/${editingArticle.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/news/writer/articles`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchArticles(token);
      setShowEditor(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;

    const token = localStorage.getItem("writerToken");
    try {
      await axios.delete(`${API}/news/writer/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchArticles(token);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete article");
    }
  };

  const handleAddTag = () => {
    if (tagInput && !form.tags.includes(tagInput)) {
      setForm({ ...form, tags: [...form.tags, tagInput] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  // Article Editor View
  if (showEditor) {
    return (
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="bg-zinc-900 border-b border-white/10 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <button
              onClick={() => { setShowEditor(false); resetForm(); }}
              className="flex items-center gap-2 text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="font-heading text-xl font-bold text-white">
              {editingArticle ? "Edit Article" : "New Article"}
            </h1>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </header>

        {/* Editor */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Article title..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-lg focus:outline-none focus:border-white/30"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Content *</label>
              <RichTextEditor value={form.content} onChange={(content) => setForm({ ...form, content })} />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Excerpt</label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="Brief summary..."
                rows={3}
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={form.featured_image}
                  onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="General"
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Tags</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag..."
                  className="flex-1 bg-black border border-white/10 text-white px-4 py-2 focus:outline-none focus:border-white/30"
                />
                <Button type="button" onClick={handleAddTag} className="bg-zinc-800 text-white hover:bg-zinc-700">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-300 text-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="text-zinc-500 hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Status</label>
              <div className="flex gap-2">
                {["draft", "published"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setForm({ ...form, status })}
                    className={`px-4 py-2 text-sm font-bold uppercase ${
                      form.status === status
                        ? status === "published" ? "bg-green-600 text-white" : "bg-yellow-600 text-black"
                        : "bg-zinc-800 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <PenTool className="w-6 h-6 text-white" />
              <span className="font-heading text-xl font-bold text-white">Writer Portal</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {writer?.headshot_url ? (
                <img src={writer.headshot_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                  <User className="w-4 h-4 text-zinc-400" />
                </div>
              )}
              <span className="text-white">{writer?.name}</span>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase text-white">My Articles</h1>
            <p className="text-zinc-500 mt-1">{articles.length} articles</p>
          </div>
          <Button onClick={() => setShowEditor(true)} className="bg-white text-black hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>

        {articles.length === 0 ? (
          <div className="bg-zinc-900/50 border border-white/10 p-12 text-center">
            <Newspaper className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Articles Yet</h2>
            <p className="text-zinc-500 mb-6">Start writing your first article for Antler Sports Network.</p>
            <Button onClick={() => setShowEditor(true)} className="bg-zinc-800 text-white hover:bg-zinc-700">
              <Plus className="w-4 h-4 mr-2" />
              Write Your First Article
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-white/10 p-6 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {article.featured_image ? (
                    <img src={article.featured_image} alt="" className="w-24 h-16 object-cover bg-zinc-800" />
                  ) : (
                    <div className="w-24 h-16 bg-zinc-800 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-lg font-bold text-white">{article.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                        article.status === "published" ? "bg-green-500/20 text-green-500" : "bg-yellow-500/20 text-yellow-500"
                      }`}>
                        {article.status}
                      </span>
                    </div>
                    <p className="text-zinc-500 text-sm line-clamp-2 mb-2">{article.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-zinc-600">
                      <span>{article.category}</span>
                      <span>•</span>
                      <span>{formatDate(article.created_at)}</span>
                      <span>•</span>
                      <span>{article.views || 0} views</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {article.status === "published" && (
                      <Link to={`/news/${article.slug}`} target="_blank" className="p-2 text-zinc-500 hover:text-white" title="View">
                        <Eye className="w-5 h-5" />
                      </Link>
                    )}
                    <button onClick={() => handleEdit(article)} className="p-2 text-zinc-500 hover:text-white" title="Edit">
                      <Edit className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(article.id)} className="p-2 text-zinc-500 hover:text-red-500" title="Delete">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default WriterPortal;
